// =============================================================================
// services/fs.ts — bare-RN filesystem adapter (blueprint §1 swap #15).
//
// MIGRATION: expo-file-system/legacy → react-native-fs. The ONLY consumer is the
// E2E MEDIA path in services/e2e.ts (encrypt a local file's bytes → upload an
// opaque blob; download a ciphertext blob → decrypt to a local file). That path
// needs exactly five operations:
//   - readAsStringAsync(uri, base64)      → RNFS.readFile(path,'base64')
//   - writeAsStringAsync(uri, b64, base64)→ RNFS.writeFile(path, b64,'base64')
//   - downloadAsync(url, dest)            → RNFS.downloadFile({fromUrl,toFile})
//   - deleteAsync(uri, {idempotent})      → RNFS.unlink(path) (swallow ENOENT)
//   - cacheDirectory                      → RNFS.CachesDirectoryPath + '/'
//
// react-native-fs (`@dr.pogodin/react-native-fs`, the New-Arch fork) is a
// P3-added native dep declared in package.json; the pod is brought in by the
// batch `pod install`. It is loaded via a GUARDED require so this module — and
// e2e.ts, and every DM screen — type-checks and runs even before the pod is
// installed: every op then resolves to a graceful failure (null / no-op), so the
// E2E MEDIA feature simply falls back to its plaintext path (e2e.ts is built to
// treat any fs failure as "skip encryption / show 🔒 placeholder", never a crash).
//
// react-native-fs works with PLAIN FILE PATHS, not file:// URIs, so we normalise:
// strip a leading `file://` from any uri before handing it to the lib, and the
// `cacheDirectory` we expose is a bare path (no scheme) — exactly what the lib
// returns + what multipart upload-by-path expects.
// =============================================================================

// Encoding sentinels mirror expo-file-system's EncodingType so e2e.ts keeps the
// same call shape (FileSystem.EncodingType.Base64 → EncodingType.Base64).
export const EncodingType = { Base64: 'base64', UTF8: 'utf8' } as const;
export type Encoding = (typeof EncodingType)[keyof typeof EncodingType];

let _mod: any = null;
let _tried = false;

function lib(): any {
  if (_tried) return _mod;
  _tried = true;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    _mod = require('@dr.pogodin/react-native-fs');
    _mod = _mod?.default ?? _mod;
  } catch {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      _mod = require('react-native-fs');
      _mod = _mod?.default ?? _mod;
    } catch {
      _mod = null;
    }
  }
  return _mod;
}

/** True once a filesystem lib is actually present (pods installed). */
export function isAvailable(): boolean {
  return !!lib();
}

// RNFS wants raw filesystem paths; e2e.ts may pass either a path or a file:// uri
// (image-picker / recorder uris are file://). Strip the scheme defensively.
function toPath(uri: string): string {
  if (!uri) return uri;
  return uri.startsWith('file://') ? uri.slice('file://'.length) : uri;
}

/**
 * The app cache directory as a bare path WITH a trailing slash, so callers can do
 * `${cacheDirectory}enc_xxx.bin` exactly as they did with expo-file-system. Empty
 * string if no fs lib is present (callers guard for that → plaintext fallback).
 */
export function cacheDirectory(): string {
  const m = lib();
  const dir = m?.CachesDirectoryPath;
  return typeof dir === 'string' && dir ? `${dir}/` : '';
}

/** Read a file's bytes as a base64 string. Returns null on any failure. */
export async function readAsStringAsync(
  uri: string,
  opts?: { encoding?: Encoding }
): Promise<string | null> {
  try {
    const m = lib();
    if (!m?.readFile) return null;
    const encoding = opts?.encoding ?? EncodingType.Base64;
    return await m.readFile(toPath(uri), encoding);
  } catch {
    return null;
  }
}

/** Write a (base64) string to a local file. Resolves false on any failure. */
export async function writeAsStringAsync(
  uri: string,
  contents: string,
  opts?: { encoding?: Encoding }
): Promise<boolean> {
  try {
    const m = lib();
    if (!m?.writeFile) return false;
    const encoding = opts?.encoding ?? EncodingType.Base64;
    await m.writeFile(toPath(uri), contents, encoding);
    return true;
  } catch {
    return false;
  }
}

/**
 * Download a remote url to a local path. Returns { uri, status } so callers can
 * branch on HTTP status exactly like expo-file-system's downloadAsync did. On any
 * failure returns { uri, status: 0 } (caller treats >=400 / 0 as failure).
 */
export async function downloadAsync(
  url: string,
  destUri: string
): Promise<{ uri: string; status: number } | null> {
  try {
    const m = lib();
    if (!m?.downloadFile) return null;
    const toFile = toPath(destUri);
    const job = m.downloadFile({ fromUrl: url, toFile });
    const result = await job.promise;
    return { uri: destUri, status: typeof result?.statusCode === 'number' ? result.statusCode : 200 };
  } catch {
    return null;
  }
}

/** Best-effort delete; never throws. `idempotent` swallows a missing-file error. */
export async function deleteAsync(
  uri: string,
  _opts?: { idempotent?: boolean }
): Promise<void> {
  try {
    const m = lib();
    if (!m?.unlink) return;
    await m.unlink(toPath(uri));
  } catch {
    // idempotent: a missing file (ENOENT) is fine; any error is non-fatal.
  }
}

export default {
  EncodingType,
  isAvailable,
  cacheDirectory,
  readAsStringAsync,
  writeAsStringAsync,
  downloadAsync,
  deleteAsync,
};
